import React from "react";
import { useTranslation } from "react-i18next";
import DocTypeIcon, { DOC_CATEGORIES } from "./DocTypeIcon";

/**
 * 8-card category grid surfacing portfolio distribution at a glance.
 *
 * The `all` card stays first and is styled in QENVA Deep Navy when
 * selected (matching the Brand Kit reference). Each other card uses the
 * pastel palette from `DocTypeIcon` so the colour coding stays consistent
 * across the whole module.
 *
 * Props:
 *   counts   : { [doc_type]: number } — including the `all` synthetic key
 *   selected : doc_type key currently active (defaults to "all")
 *   onSelect : (key) => void
 */
export default function CategoryGrid({ counts = {}, selected = "all", onSelect }) {
  const { t } = useTranslation("documents");
  return (
    <section
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-8 gap-3 lg:gap-4 mb-8"
      data-testid="category-grid"
    >
      {DOC_CATEGORIES.map((cat) => {
        const isActive = selected === cat.key;
        const count = counts[cat.key] ?? 0;
        const isAll = cat.key === "all";
        const Icon = cat.icon;
        return (
          <button
            key={cat.key}
            type="button"
            onClick={() => onSelect?.(cat.key)}
            data-testid={`category-card-${cat.key}`}
            className={`group relative text-left p-4 rounded-xl border transition-all duration-150 min-w-0 hover:-translate-y-[1px] ${
              isActive
                ? "bg-qenva-navy text-white border-qenva-navy-line shadow-[0_18px_40px_-22px_rgba(11,35,65,0.5)]"
                : "bg-white border-zinc-200 hover:border-zinc-300 hover:shadow-sm"
            }`}
          >
            <div className="flex items-start justify-between mb-7 gap-2">
              <div
                className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
                  isActive ? "bg-white/10" : cat.bgClass
                }`}
              >
                <Icon
                  size={16}
                  strokeWidth={1.75}
                  className={isActive ? "text-white" : cat.colorClass}
                />
              </div>
              <span
                className={`text-[12px] font-bold tabular-nums px-2 py-0.5 rounded-md min-w-[26px] text-center ${
                  isActive ? "bg-white/15 text-white" : "bg-zinc-100 text-zinc-700"
                }`}
                title={String(count)}
              >
                {count}
              </span>
            </div>
            <p
              className={`text-[13px] font-semibold tracking-tight truncate ${
                isActive ? "text-white" : "text-qenva-navy"
              }`}
              title={t(`category.${cat.key}`)}
            >
              {t(`category.${cat.key}`)}
            </p>
            {!isAll && (
              <p
                className={`text-[11px] mt-0.5 truncate ${
                  isActive ? "text-white/60" : "text-zinc-500"
                }`}
              >
                {count === 0 ? t("category.empty_hint") : t("category.docs_label", { count })}
              </p>
            )}
            {isAll && (
              <p className={`text-[11px] mt-0.5 truncate ${isActive ? "text-white/60" : "text-zinc-500"}`}>
                {t("category.all_hint")}
              </p>
            )}
          </button>
        );
      })}
    </section>
  );
}
